import { AfterViewInit, ChangeDetectionStrategy, Component, ViewChild } from '@angular/core';
import { MatDrawer } from '@angular/material/sidenav';
import { Store } from '@ngrx/store';

import { toggleHelp } from '../../../+state/app.actions';
import { getHelpState } from '../../../+state/app.selectors';
import { GopherBuddyAppState } from '../../../+state/app.state';
import {
  DeviceManagedState,
  StateStatus,
} from '../../../../background-service-worker/services/extension-store/interfaces/extension-store.interfaces';
import { GBMessages } from '../../../../background-service-worker/services/message-handler/message-handler.interfaces';
import { UserAccessType } from '../../../../background-service-worker/services/user-profile/user-profile.state';
import { UiService } from '../../services/ui.service';

@Component({
  selector: 'app-launcher',
  templateUrl: './launcher.component.html',
  styleUrls: ['./launcher.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
})
export class LauncherComponent implements AfterViewInit {
  public cacheClearing = false;

  @ViewChild('helpDrawer') helpDrawer!: MatDrawer;

  constructor(public appState: Store<GopherBuddyAppState>, public uiService: UiService) {}

  public ngAfterViewInit(): void {
    this.appState.select(getHelpState).subscribe(async (helpState: boolean): Promise<void> => {
      if (helpState) {
        await this.helpDrawer.open();
        return;
      }
      await this.helpDrawer.close();
    });

    // If the user closes the infoDrawer mark all toggle-able Panels as closed.
    this.helpDrawer.openedChange.subscribe((isOpen: boolean) => {
      if (!isOpen) {
        this.appState.dispatch(toggleHelp({ open: false }));
      }
    });
  }

  public isCheckedIn = (appState: GopherBuddyAppState): boolean =>    
    appState.app.settings?.checkInState.status === StateStatus.SUCCESS;

  public isLoaded = (appState: GopherBuddyAppState): boolean =>
    appState.app.settings?.globalAppFlagsState.isLoaded === true;

  public isValidCustomer = (appState: GopherBuddyAppState): boolean =>
    appState.app.settings?.checkInState.isCustomerValid ?? false;

  public isManaged = (appState: GopherBuddyAppState): boolean =>
    appState.app.settings?.globalAppFlagsState.deviceManagedState === DeviceManagedState.MANAGED;

  public hasErrors = (appState: GopherBuddyAppState): boolean =>
    appState.app.settings?.checkInState.status === StateStatus.FAILED;

  public hasDeviceInfoAccess = (appState: GopherBuddyAppState): boolean =>
    appState.app.settings?.userProfile.userProfile?.accessLevel === UserAccessType.BASIC ||
    appState.app.settings?.userProfile.userProfile?.accessLevel === UserAccessType.FULL;

  public checkVersionEnabled = (appState: GopherBuddyAppState): boolean =>
    appState.app.settings?.customerState.customerSettings?.settings.allowChromeOsUpdateReminders ?? false;

  public versionIsCurrent = (appState: GopherBuddyAppState): boolean =>
    appState.app.settings?.versionUpdateState.updateNeeded === false;

  public lastVersionCheck = (appState: GopherBuddyAppState): Date =>
    new Date(appState.app.settings?.checkInState.lastCheckIn ?? Date.now());

  public clearCache = (): void => {
    this.cacheClearing = true;
    chrome.runtime.sendMessage(GBMessages.CLEAR_CACHE, (response: { success: boolean }) => {
      this.cacheClearing = false;
      const message = response.success
        ? 'Your device cache has successfully been cleared.'
        : 'Your device cache failed to clear';
      this.uiService.showNotification('clear_cache', message);
    });
  };

  public openDeviceDetails = (): void => {
    chrome.runtime.sendMessage(GBMessages.OPEN_DEVICE_INFO, () => {});
  };

  public openUpdateHelp = (): void => {
    chrome.runtime.sendMessage(GBMessages.OPEN_HELP, () => {});
  };

  public onFixVersion = (): void => {
    chrome.runtime.sendMessage(GBMessages.FIX_VERSION, () => {});
  };
}
